import { gql, useLazyQuery } from '@apollo/client'
import axios from 'axios'
import moment from 'moment'
import { useCallback, useMemo, useRef, useState } from 'react'
import { v4 as uuidV4 } from 'uuid'

import useBrands from 'pared/layouts/hooks/useBrands'

import useInputValue, {
  IDataType as IInputValueDataType,
} from '../common/useInputValue'
import useSelectOptions, {
  IDataType as ISelectOptionsDataType,
} from '../common/useSelectOptions'

export interface IDataType extends IInputValueDataType, ISelectOptionsDataType {
  uploadReport: {
    disable: boolean
    loading: boolean
    onClick: () => Promise<void>
    errorMessage?: string
    infoMessage?: string
    overwriteConfirm: {
      open: boolean
      onClose: () => void
      submit: () => Promise<void>
    }
  }
}

interface IQueryDataType {
  customizedData: {
    nodes: {
      data: Record<string, string> & { uploadId: string }
    }[]
  }
}

const query = gql`
  query CheckReportStatus($iCustomizationType: String!) {
    customizedData(
      condition: { customizationType: $iCustomizationType }
      last: 1
    ) {
      nodes {
        data
      }
    }
  }
`

const useChecReportExist = () => {
  const [loading, setLoading] = useState(false)
  const { brand } = useBrands()

  return {
    loading,
    isReportExist: useCallback(
      async (filename) => {
        if (loading) return

        setLoading(true)

        const queryObject = new URLSearchParams()

        queryObject.append('filename', filename)
        queryObject.append('customerCode', brand)

        try {
          const response = await axios.head(
            `${
              process.env.REACT_APP_BE_BASE_URL
            }/upload?${queryObject.toString()}`,
            {
              validateStatus: (status) => status >= 200 && status < 500,
            },
          )

          setLoading(false)

          return response.status === 200
        } catch (e) {
          setLoading(false)

          return null
        }
      },
      [loading, setLoading, brand],
    ),
  }
}

// why: returns false instead of throwing on timeout - the logger record is
// only written after the lambda finishes the whole ingest, so a long ingest
// (e.g. a multi-period R365 P&L file takes minutes) legitimately outlives
// this polling window and must read as "still processing", not as a failure
const checkReportStatusLoop = async (
  checker: () => Promise<boolean>,
  count = 0,
): Promise<boolean> => {
  if (count > 10) return false

  const result = await checker()

  if (result) return true

  await new Promise((resolve) => setTimeout(resolve, 500))

  return checkReportStatusLoop(checker, count + 1)
}

const useUpload = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [infoMessage, setInfoMessage] = useState<string | undefined>()
  const { brand } = useBrands()
  const [checkReportStatus, { client }] = useLazyQuery<IQueryDataType>(query, {
    variables: {
      iCustomizationType: 'UPLOAD_REPORT_LOGGER_INFO',
    },
    fetchPolicy: 'network-only',
  })

  return {
    loading,
    error,
    infoMessage,
    upload: useCallback(
      async (data: Record<string, string | Blob> & { uploadId: string }) => {
        if (loading) return

        setLoading(true)
        setError(undefined)
        setInfoMessage(undefined)

        const formData = new FormData()

        Object.keys(data).forEach((key) => {
          formData.append(key, data[key])
        })
        formData.append('customerCode', brand)

        try {
          await axios.post(
            `${process.env.REACT_APP_BE_BASE_URL}/upload`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          )

          const confirmed = await checkReportStatusLoop(
            async (): Promise<boolean> =>
              data.uploadId ===
              (
                await checkReportStatus()
              ).data?.customizedData.nodes[0]?.data.uploadId,
          )

          if (!confirmed)
            setInfoMessage(
              'Upload received - ingest is still running. Check the Upload Events table below in a few minutes.',
            )

          await client.refetchQueries({
            include: ['UploadReportStatus'],
          })
        } catch (e) {
          setError((e as Error).message)
        }

        setLoading(false)
      },
      [
        loading,
        setLoading,
        setError,
        setInfoMessage,
        brand,
        checkReportStatus,
        client,
      ],
    ),
  }
}

interface IReportTypeConfigType {
  extension: 'xlsx' | 'csv'
  buildFilename: () => string
}

const REPORT_TYPE_CONFIGS: Record<string, IReportTypeConfigType> = {
  'Labor Tracker Roster': {
    extension: 'xlsx',
    buildFilename: () =>
      `Labor Tracker Roster - ${moment().format('YYYY-MM-DD')}.xlsx`,
  },
  'R365 P&L': {
    extension: 'xlsx',
    buildFilename: () => `R365 P&L - ${moment().format('YYYY-MM-DD')}.xlsx`,
  },
  'Uber Eats': {
    extension: 'csv',
    // sync: kit-core server/src/routes/upload/uploadReport.ts
    // sync: kit-core lib/ubereat/src/ingest/payment.ts
    // why: this filename is a backend contract - the server regex-extracts the
    // first YYYY-MM-DD date for the lambda payload and the ingest uses the full
    // filename as the S3 key, so any format change breaks the Uber Eats ingest.
    // The FW-WS prefix must match the DCM setting other_info.customerPrefix.
    buildFilename: () => {
      const date = moment().format('YYYY-MM-DD')

      return `FW-WS-UBEREAT-${date}_${date}-PAYMENTS.csv`
    },
  },
}

const useUploadReport = () => {
  const { selectOptions } = useSelectOptions({
    options: {
      uploadReportType: {
        defaultId: [['Labor Tracker Roster']],
        options: Object.keys(REPORT_TYPE_CONFIGS).map((reportType) => ({
          id: reportType,
          displayName: reportType,
        })),
      },
    },
  })
  const { inputValue } = useInputValue({
    fields: ['file'],
  })
  const { loading: checkReportExistLoading, isReportExist } =
    useChecReportExist()
  const { loading: uploadLoading, error, infoMessage, upload } = useUpload()
  const loading = checkReportExistLoading || uploadLoading
  const [showOverwriteModal, setShowOverwriteModal] = useState(false)
  const [validationError, setValidationError] = useState<string | undefined>()
  const skipCheckRef = useRef(false)

  return {
    selectOptions,
    inputValue,
    uploadReport: useMemo((): IDataType['uploadReport'] => {
      const file = inputValue?.file.value?.[0]
      const disable =
        !selectOptions?.uploadReportType.displayName || !file || loading
      const onClick = async () => {
        if (disable) return

        const uploadReportType =
          selectOptions.uploadReportType.displayName[0][0]
        const { extension, buildFilename } =
          REPORT_TYPE_CONFIGS[uploadReportType]
        const originalFilename = file instanceof File ? file.name : ''

        if (!new RegExp(`\\.${extension}$`, 'i').test(originalFilename)) {
          setValidationError(`${uploadReportType} must be a .${extension} file`)
          return
        }

        setValidationError(undefined)

        const filename = buildFilename()

        if (
          !skipCheckRef.current &&
          (await isReportExist(filename)) !== false
        ) {
          setShowOverwriteModal(true)
          return
        }

        await upload({
          uploadId: uuidV4(),
          filename,
          type: uploadReportType,
          uploadDate: moment.utc().format('YYYY-MM-DD'),
          file,
        })
      }

      return {
        disable,
        loading,
        onClick,
        errorMessage: validationError || error,
        infoMessage,
        overwriteConfirm: {
          open: showOverwriteModal,
          onClose: () => setShowOverwriteModal(false),
          submit: async () => {
            skipCheckRef.current = true
            setShowOverwriteModal(false)
            await onClick()
            skipCheckRef.current = false
          },
        },
      }
    }, [
      selectOptions,
      inputValue,
      loading,
      error,
      infoMessage,
      validationError,
      setValidationError,
      isReportExist,
      upload,
      showOverwriteModal,
      setShowOverwriteModal,
      skipCheckRef,
    ]),
  }
}

export default useUploadReport
